import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../helper/appRoutes';
import {
  ACCENT,
  BORDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BG_SUBTLE,
  AdminHomeDashboardTile,
} from '../components/Elements/AdminHomeDashboardTile';

const SignatureIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 44H52M18 36L38 16L46 24L26 44H18V36Z"
      stroke={ACCENT}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StampedDocumentIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M40 8H16C13.79 8 12 9.79 12 12V52C12 54.21 13.79 56 16 56H48C50.21 56 52 54.21 52 52V20L40 8Z"
      stroke={TEXT_SECONDARY}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M40 8V20H52" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="18" y="40" width="28" height="10" rx="2" stroke={ACCENT} strokeWidth="2" fill="none" />
  </svg>
);

const TagReportIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22 12H34L46 24V46C46 48.21 44.21 50 42 50H22C19.79 50 18 48.21 18 46V16C18 13.79 19.79 12 22 12Z"
      stroke={ACCENT}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="28" cy="28" r="4" stroke={ACCENT} strokeWidth="2" />
  </svg>
);

const LibrarySearchIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 14H28V50H12V14ZM36 14H52V50H36V14Z"
      stroke={ACCENT}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M20 22H24M40 22H44M20 30H24M40 30H44" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const AdminIWantTo: React.FC = () => {
  const navigate = useNavigate();

  const IWantToItems = [
    {
      icon: <SignatureIcon />,
      title: 'Request a Signature',
      iconBgColor: '#f0f9ff',
      onClick: () => navigate(AppRoutes.home.requestSignature),
    },
    {
      icon: <StampedDocumentIcon />,
      title: 'Send a Stamped Document',
      iconBgColor: '#f1f5f9',
      onClick: () => navigate(AppRoutes.home.sendStampedDocument),
    },
    {
      icon: <TagReportIcon />,
      title: 'Report a Tag',
      iconBgColor: '#f0f9ff',
      onClick: () => navigate(AppRoutes.home.reportTag),
    },
    {
      icon: <LibrarySearchIcon />,
      title: 'Go to Diligent Search Library',
      iconBgColor: '#f0f9ff',
      onClick: () => navigate(AppRoutes.home.diligentSearchLibrary),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 85px)', md: 'calc(100vh - 250px)' },
        backgroundColor: BG_SUBTLE,
        paddingTop: { xs: '40px', md: '84px' },
        marginLeft: { xs: '-24px', md: '0px' },
        marginRight: { xs: '-24px', md: '0px' },
        paddingBottom: 3,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            borderBottom: `1px solid ${BORDER}`,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            mb: 3,
            px: 3,
            py: 2.5,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            border: `1px solid ${BORDER}`,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: TEXT_PRIMARY,
              mb: 0.25,
            }}
          >
            I Want To
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 400,
              color: TEXT_SECONDARY,
            }}
          >
            Choose an action below to open its workflow.
          </Typography>
        </Box>

        {/*
          Two columns on md+ (2×2), without widening tiles: cap each column ~same as old md={4} (~370px in lg).
          xs: one column; sm: two equal columns (tablet).
        */}
        <Box
          sx={{
            display: 'grid',
            width: '100%',
            gap: 2,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(2, minmax(0, 370px))',
            },
            justifyContent: 'center',
          }}
        >
          {IWantToItems.map((page, idx) => (
            <Box key={idx} sx={{ minWidth: 0 }}>
              <AdminHomeDashboardTile
                icon={page.icon}
                title={page.title}
                onClick={page.onClick}
                iconBgColor={page.iconBgColor}
              />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AdminIWantTo;
